/**
 * MCUboot image inspection.
 *
 * Firmware uploaded at Step 1 of the New Baseline wizard is a complete MCUboot
 * image, signed (and payload-encrypted) by the build system. The signature is an
 * ECDSA TLV record in the trailer, so "is this signed?" is answerable by reading
 * integers out of the buffer — no key material and no crypto are involved.
 *
 * Image layout:
 *
 *   0x000  struct image_header (32 bytes used of hdr_size)
 *   hdr_size .. +img_size      payload
 *   then                       protected TLV area   (info magic 0x6908)
 *   then                       unprotected TLV area (info magic 0x6907)
 *
 * Each TLV area is `info_magic u16, total u16` followed by entries of
 * `tag u8, pad u8, len u16, value[len]`. `total` counts the 4-byte info header.
 *
 * Note: the SHA256/ECDSA TLVs are computed over the *plaintext* payload, but the
 * payload on disk is ECIES-encrypted (IMAGE_F_ENCRYPTED_AES128). Verifying the
 * signature arithmetic would require the enc-ec256 *private* key, which is a
 * device secret and is not available here. Presence of the signature plus the
 * key-identity check below is the strongest verification possible client-side.
 */

export const MCUBOOT_MAGIC = 0x96f3b83d;

const HEADER_MIN_BYTES = 32;

const TLV_INFO_PROT = 0x6908;
const TLV_INFO_UNPROT = 0x6907;
const TLV_INFO_HEADER_BYTES = 4;
const TLV_ENTRY_HEADER_BYTES = 4;

const TLV_KEYHASH = 0x01;
const TLV_ECDSA_SIG = 0x22;
const TLV_SEC_CNT = 0x50;

// IMAGE_F_ENCRYPTED_AES128 — payload was encrypted by `imgtool sign --encrypt`.
const IMAGE_F_ENCRYPTED_AES128 = 0x04;
const IMAGE_F_ENCRYPTED_AES256 = 0x08;

/**
 * SHA-256 of the SubjectPublicKeyInfo DER of the public half of
 * services/device/signing-file/keys/ecdsa-p256-signing-key.pem.
 *
 * This is the hash of a *public* key, so embedding it in the bundle discloses
 * nothing. MCUboot stores the same value in the KEYHASH TLV, which lets us prove
 * the image was signed by this project's key rather than merely signed by
 * someone.
 */
export const EXPECTED_KEYHASH =
  "4388bfc285ddc874e0dc9fcc9b0cccf2675c05da8bdc3346acdc223de331dd40";

const REASON_NOT_AN_IMAGE =
  "Not an MCUboot firmware image — the file is missing its image header. It may be a raw binary or the wrong file.";
const REASON_TRUNCATED = "Firmware image is truncated or corrupt.";
const REASON_UNSIGNED =
  "Firmware is not signed — no ECDSA signature was found in the image. Sign the build before uploading.";
const REASON_UNTRUSTED_KEY =
  "Firmware is signed with an unrecognised key and does not match this project's signing key.";

function toHex(bytes) {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Collect every TLV record in the areas following the payload.
 * Returns null if the trailer is malformed.
 */
function readTlvs(view, start) {
  const tlvs = new Map();
  let offset = start;

  while (offset + TLV_INFO_HEADER_BYTES <= view.byteLength) {
    const infoMagic = view.getUint16(offset, true);
    if (infoMagic !== TLV_INFO_PROT && infoMagic !== TLV_INFO_UNPROT) break;

    const total = view.getUint16(offset + 2, true);
    // A total that does not clear its own info header, or that runs past the end
    // of the buffer, means the trailer is not trustworthy.
    if (total <= TLV_INFO_HEADER_BYTES) return null;
    const areaEnd = offset + total;
    if (areaEnd > view.byteLength) return null;

    let p = offset + TLV_INFO_HEADER_BYTES;
    while (p + TLV_ENTRY_HEADER_BYTES <= areaEnd) {
      const tag = view.getUint8(p);
      const len = view.getUint16(p + 2, true);
      const valueStart = p + TLV_ENTRY_HEADER_BYTES;
      if (valueStart + len > areaEnd) return null;

      if (!tlvs.has(tag)) {
        tlvs.set(tag, new Uint8Array(view.buffer, view.byteOffset + valueStart, len));
      }
      p = valueStart + len;
    }

    // Guarantee forward progress regardless of the values in the file.
    if (areaEnd <= offset) return null;
    offset = areaEnd;
  }

  return tlvs;
}

/**
 * Inspect an uploaded firmware buffer.
 *
 * @param {ArrayBuffer} arrayBuffer raw bytes of the .bin
 * @returns {{
 *   valid: boolean, signed: boolean, keyTrusted: boolean,
 *   version: string|null, securityCounter: number|null, encrypted: boolean,
 *   headerSize: number|null, imageSize: number|null, reason: string|null
 * }}
 */
export function inspectFirmware(arrayBuffer) {
  const result = {
    valid: false,
    signed: false,
    keyTrusted: false,
    version: null,
    securityCounter: null,
    encrypted: false,
    headerSize: null,
    imageSize: null,
    reason: null,
  };

  if (!arrayBuffer || arrayBuffer.byteLength < HEADER_MIN_BYTES) {
    result.reason = REASON_NOT_AN_IMAGE;
    return result;
  }

  const view = new DataView(arrayBuffer);

  if (view.getUint32(0, true) !== MCUBOOT_MAGIC) {
    result.reason = REASON_NOT_AN_IMAGE;
    return result;
  }

  const headerSize = view.getUint16(8, true);
  const imageSize = view.getUint32(12, true);
  const flags = view.getUint32(16, true);

  result.headerSize = headerSize;
  result.imageSize = imageSize;
  result.encrypted = (flags & (IMAGE_F_ENCRYPTED_AES128 | IMAGE_F_ENCRYPTED_AES256)) !== 0;

  const major = view.getUint8(20);
  const minor = view.getUint8(21);
  const revision = view.getUint16(22, true);
  const build = view.getUint32(24, true);
  result.version = `${major}.${minor}.${revision}+${build}`;

  const payloadEnd = headerSize + imageSize;
  if (headerSize < HEADER_MIN_BYTES || payloadEnd > arrayBuffer.byteLength) {
    result.reason = REASON_TRUNCATED;
    return result;
  }

  const tlvs = readTlvs(view, payloadEnd);
  if (tlvs === null) {
    result.reason = REASON_TRUNCATED;
    return result;
  }

  const secCnt = tlvs.get(TLV_SEC_CNT);
  if (secCnt && secCnt.length === 4) {
    result.securityCounter = new DataView(
      secCnt.buffer,
      secCnt.byteOffset,
      secCnt.length
    ).getUint32(0, true);
  }

  if (!tlvs.has(TLV_ECDSA_SIG)) {
    result.reason = REASON_UNSIGNED;
    return result;
  }
  result.signed = true;

  const keyhash = tlvs.get(TLV_KEYHASH);
  result.keyTrusted = Boolean(keyhash) && toHex(keyhash) === EXPECTED_KEYHASH;
  if (!result.keyTrusted) {
    result.reason = REASON_UNTRUSTED_KEY;
    return result;
  }

  result.valid = true;
  return result;
}
